/**
 * @packageDocumentation
 * @module      CorPageEMCTest.ts
 * @description The EMC UI page in the Cloud application.
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CorPageEmcTest_1;
import { LitElement, customElement, html, css, property } from 'lit-element';
import { universalStyles } from './CorMain';
import { EmcSection, Subsystems } from './emc/EmcEnums';
import { EmcDisplayFacade } from './emc/EmcDisplayFacade';
import { EMCDisplayMessageConsts } from './emc/EMCDisplayMessageConsts';
import EmcConsts from './emc/EmcConsts';
import CorThemeSelector from './CorThemeSelector';
let CorPageEmcTest = CorPageEmcTest_1 = class CorPageEmcTest extends LitElement {
    constructor() {
        super(...arguments);
        this.emcDisplayFacade = EmcDisplayFacade.getEmcDisplayFacade();
        this.pageContents = html ``;
        this.activeSection = this.emcDisplayFacade.activeSection;
        this.addingSystems = false;
        this.warningModalTitle = "";
        this.hasWarning = false;
        this.callAfterWarningClose = null;
        // Aggregated messages from all active systems
        this.messageText = this.emcDisplayFacade.messageText;
        // Idle Monitor properties and methods
        this.isIdleMonitorSelected = this.emcDisplayFacade.isIdleMonitorSelected;
        this.isIdleMonitorRunning = this.emcDisplayFacade.isIdleMonitorRunning;
        this.idleRuntime = this.emcDisplayFacade.idleRuntime;
        this.startIdleMonitorTimeStamp = this.emcDisplayFacade.startIdleMonitorTimeStamp;
        // EMC Automation properties and methods
        this.isEmcAutomationSelected = this.emcDisplayFacade.isEmcAutomationSelected;
        this.isEMCAutomationRunning = this.emcDisplayFacade.isEMCAutomationRunning;
        this.emcAutomationRuntime = this.emcDisplayFacade.emcAutomationRuntime;
        this.startEmcAutomationTimestamp = this.emcDisplayFacade.startEmcAutomationTimestamp;
        // Full system
        this.emcFullSystemSelected = this.emcDisplayFacade.emcFullSystemSelected;
        // The systems currently active whose live data and messages need to be fetched periodically    
        this.activeSystemsList = this.emcDisplayFacade.activeSystemsList;
        this.patientSystemCount = this.emcDisplayFacade.patientSystemCount;
        this.controlSystemCount = this.emcDisplayFacade.controlSystemCount;
        // Segments of live data
        this.patientSubsystemsVersionData = this.emcDisplayFacade.patientSubsystemsVersionData;
        this.controlSubsystemsVersionData = this.emcDisplayFacade.controlSubsystemsVersionData;
        this.arm = this.emcDisplayFacade.arm;
        this.drive = this.emcDisplayFacade.drive;
        this.frame = this.emcDisplayFacade.frame;
        this.csPdo = this.emcDisplayFacade.csPdo;
        this.psPdo = this.emcDisplayFacade.psPdo;
        this.latency = this.emcDisplayFacade.latency;
        this.controlConnectionStatus = this.emcDisplayFacade.controlConnectionStatus;
        this.communicationFaultCount = this.emcDisplayFacade.communicationFaultCount;
        this.pim = this.emcDisplayFacade.pim;
        this.userInput = this.emcDisplayFacade.userInput;
        // Warning and fault counts
        this.warningCountDrive = this.emcDisplayFacade.warningCountDrive;
        this.faultCountDrive = this.emcDisplayFacade.faultCountDrive;
        this.warningCountArm = this.emcDisplayFacade.warningCountArm;
        this.faultCountArm = this.emcDisplayFacade.faultCountArm;
        this.warningCountFrame = this.emcDisplayFacade.warningCountFrame;
        this.faultCountFrame = this.emcDisplayFacade.faultCountFrame;
        this.warningCountPSRC = this.emcDisplayFacade.warningCountPSRC;
        this.faultCountPSRC = this.emcDisplayFacade.faultCountPSRC;
        this.warningCountPSIC = this.emcDisplayFacade.warningCountPSIC;
        this.faultCountPSIC = this.emcDisplayFacade.faultCountPSIC;
        this.warningCountPSUI = this.emcDisplayFacade.warningCountPSUI;
        this.faultCountPSUI = this.emcDisplayFacade.faultCountPSUI;
        this.warningCountPSSC = this.emcDisplayFacade.warningCountPSSC;
        this.faultCountPSSC = this.emcDisplayFacade.faultCountPSSC;
        this.warningCountCSRC = this.emcDisplayFacade.warningCountCSRC;
        this.faultCountCSRC = this.emcDisplayFacade.faultCountCSRC;
        this.warningCountCSIC = this.emcDisplayFacade.warningCountCSIC;
        this.faultCountCSIC = this.emcDisplayFacade.faultCountCSIC;
        this.warningCountCSUI = this.emcDisplayFacade.warningCountCSUI;
        this.faultCountCSUI = this.emcDisplayFacade.faultCountCSUI;
        this.warningCountCSSC = this.emcDisplayFacade.warningCountCSSC;
        this.faultCountCSSC = this.emcDisplayFacade.faultCountCSSC;
        this.warningCountPIM = this.emcDisplayFacade.warningCountPIM;
        this.faultCountPIM = this.emcDisplayFacade.faultCountPIM;
        this.csrc = this.emcDisplayFacade.csrc;
        this.psrc = this.emcDisplayFacade.psrc;
        this.interval = null;
        this.worker = (() => {
            const setInterval = `
      setInterval(() => {
        postMessage("updateProperties");
      }, ${EmcConsts.REFRESH_INTERVAL});
    `;
            const blob = new Blob([setInterval], { type: "text/javascript" });
            return new Worker(URL.createObjectURL(blob));
        })();
    }
    get currentTheme() {
        return CorThemeSelector.getTheme();
    }
    static get styles() {
        return css `
      ${universalStyles}

      #card {
        width: 100%;
      }
      
      sh-text {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }

      sh-page {
        width: 100%;
      }

      #drive {
        display: block;
        height:auto;
        margin-bottom:10px;
        padding: 0px;
        margin: 0px;
      }

      .field {
        display:flex;
        margin:0px;
      }

      .fieldValue {
        width: 60%;
      }

      .grid-container {
        display: grid;
        gap: 16px;
      }

      .system-status-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      .drive-grid {
        grid-template-columns: repeat(5, minmax(0, 1fr));
      }

      .drive-inner-content {
        width: 100%;
        margin-top: 8px;
        padding: 8px 0;
      }

      .drive-main-card > div > .drive-inner-content:first-child {
        margin-top: 0;
      }

      #SubSystemErrorBadge{
        --sh-badge-height: 24px;
        --sh-badge-min-width: 24px;
        --sh-badge-line-height: 1.5;
        --sh-badge-font-size: 12px;
      }
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.worker.onmessage = (event) => {
            if (event.data == "updateProperties") {
                this.updateProperties();
            }
        };
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        clearInterval(this.interval);
        this.interval = null;
        // Stop the worker so it does not keep calling updateProperties() on this
        // stale (disconnected) instance and overwrite the facade with old state.
        this.worker.onmessage = null;
        this.worker.terminate();
        // Write to the facade from the UI because these are user-input-driven
        this.emcDisplayFacade.activeSection = this.activeSection;
        this.emcDisplayFacade.isIdleMonitorSelected = this.isIdleMonitorSelected;
        this.emcDisplayFacade.isIdleMonitorRunning = this.isIdleMonitorRunning;
        this.emcDisplayFacade.startIdleMonitorTimeStamp = this.startIdleMonitorTimeStamp;
        this.emcDisplayFacade.isEmcAutomationSelected = this.isEmcAutomationSelected;
        this.emcDisplayFacade.isEMCAutomationRunning = this.isEMCAutomationRunning;
        this.emcDisplayFacade.startEmcAutomationTimestamp = this.startEmcAutomationTimestamp;
        this.emcDisplayFacade.emcFullSystemSelected = this.emcFullSystemSelected;
        this.emcDisplayFacade.activeSystemsList = this.activeSystemsList;
        this.emcDisplayFacade.patientSystemCount = this.patientSystemCount;
        this.emcDisplayFacade.controlSystemCount = this.controlSystemCount;
        this.emcDisplayFacade.userInput = this.userInput;
    }
    updated(_changedProperties) {
        super.updated(_changedProperties);
    }
    updateProperties() {
        // Write to the facade from the UI because these are user-input-driven
        this.emcDisplayFacade.isIdleMonitorRunning = this.isIdleMonitorRunning;
        this.emcDisplayFacade.startIdleMonitorTimeStamp = this.startIdleMonitorTimeStamp;
        this.emcDisplayFacade.isEMCAutomationRunning = this.isEMCAutomationRunning;
        this.emcDisplayFacade.startEmcAutomationTimestamp = this.startEmcAutomationTimestamp;
        this.emcDisplayFacade.calculateRunTimes();
        // Write to the UI from the facade because these are computed by the facade
        this.idleRuntime = this.emcDisplayFacade.idleRuntime;
        this.emcAutomationRuntime = this.emcDisplayFacade.emcAutomationRuntime;
        this.messageText = this.emcDisplayFacade.messageText;
        this.patientSubsystemsVersionData = this.emcDisplayFacade.patientSubsystemsVersionData;
        this.controlSubsystemsVersionData = this.emcDisplayFacade.controlSubsystemsVersionData;
        this.arm = this.emcDisplayFacade.arm;
        this.drive = this.emcDisplayFacade.drive;
        this.frame = this.emcDisplayFacade.frame;
        this.csPdo = this.emcDisplayFacade.csPdo;
        this.psPdo = this.emcDisplayFacade.psPdo;
        this.latency = this.emcDisplayFacade.latency;
        this.controlConnectionStatus = this.emcDisplayFacade.controlConnectionStatus;
        this.communicationFaultCount = this.emcDisplayFacade.communicationFaultCount;
        this.pim = this.emcDisplayFacade.pim;
        this.csrc = this.emcDisplayFacade.csrc;
        this.psrc = this.emcDisplayFacade.psrc;
        this.warningCountDrive = this.emcDisplayFacade.warningCountDrive;
        this.faultCountDrive = this.emcDisplayFacade.faultCountDrive;
        this.warningCountArm = this.emcDisplayFacade.warningCountArm;
        this.faultCountArm = this.emcDisplayFacade.faultCountArm;
        this.warningCountFrame = this.emcDisplayFacade.warningCountFrame;
        this.faultCountFrame = this.emcDisplayFacade.faultCountFrame;
        this.warningCountPSRC = this.emcDisplayFacade.warningCountPSRC;
        this.faultCountPSRC = this.emcDisplayFacade.faultCountPSRC;
        this.warningCountPSIC = this.emcDisplayFacade.warningCountPSIC;
        this.faultCountPSIC = this.emcDisplayFacade.faultCountPSIC;
        this.warningCountPSUI = this.emcDisplayFacade.warningCountPSUI;
        this.faultCountPSUI = this.emcDisplayFacade.faultCountPSUI;
        this.warningCountPSSC = this.emcDisplayFacade.warningCountPSSC;
        this.faultCountPSSC = this.emcDisplayFacade.faultCountPSSC;
        this.warningCountCSRC = this.emcDisplayFacade.warningCountCSRC;
        this.faultCountCSRC = this.emcDisplayFacade.faultCountCSRC;
        this.warningCountCSIC = this.emcDisplayFacade.warningCountCSIC;
        this.faultCountCSIC = this.emcDisplayFacade.faultCountCSIC;
        this.warningCountCSUI = this.emcDisplayFacade.warningCountCSUI;
        this.faultCountCSUI = this.emcDisplayFacade.faultCountCSUI;
        this.warningCountCSSC = this.emcDisplayFacade.warningCountCSSC;
        this.faultCountCSSC = this.emcDisplayFacade.faultCountCSSC;
        this.warningCountPIM = this.emcDisplayFacade.warningCountPIM;
        this.faultCountPIM = this.emcDisplayFacade.faultCountPIM;
    }
    createSystemStatusField(fieldName, warningsCount, errorsCount) {
        return html `<sh-card>
                    <div style="display: flex; align-items: center;">
                      <div style="flex: 1; min-width: 0;">
                        <sh-text size='super-header'>${fieldName}</sh-text>
                      </div>
                      <sh-icon ?disabled='${warningsCount === 0}' icon='warning' badge='${warningsCount}' size='l' style="padding: 0px 9px;"></sh-icon>
                      <sh-icon ?disabled='${errorsCount === 0}' icon='error' badge='${errorsCount}' size='l' style="padding: 0px 9px;"></sh-icon>
                    </div>
                </sh-card>`;
    }
    createField(fieldName, fieldValue) {
        return html `<div class="field">
                  <sh-text size='body-1' color='primary'>${fieldName}</sh-text>
                  <sh-text size='body-1' color='primary' class="fieldValue" style="text-align: right;">${fieldValue}</sh-text>
                </div>`;
    }
    ;
    createSoftwareVersionField(fieldName, fieldValue) {
        if (null == fieldValue) {
            fieldValue = EmcConsts.NOT_AVAILABLE;
        }
        return html `<sh-card>
                    <div style="display:flex; align-items: center;">
                      <div style="flex: 1; min-width: 0; overflow: visible; white-space: normal;">
                        <sh-text size='header-1' style="overflow: visible; white-space: normal; text-overflow: initial;">${fieldName}</sh-text>
                      </div>
                      <div style="flex: 0 0 auto; padding: 0 8px;">
                        <sh-text size='header-1'>-</sh-text>
                      </div>
                      <div style="flex: 1; min-width: 0;">
                        <sh-text size=${fieldValue === EmcConsts.NOT_AVAILABLE ? 'body-1' : 'header-1'} color=${fieldValue === EmcConsts.NOT_AVAILABLE ? 'primary' : ''}>${fieldValue}</sh-text>
                      </div>
                    </div>
                </sh-card>`;
    }
    systemStatusTabView() {
        this.activeSection = EmcSection.systemStatus;
        this.pageContents = html `
    <div class="grid-container system-status-grid">
      <sh-card id='mechanical-subsystems-view'>
        ${this.createSystemStatusField("Arm", this.warningCountArm, this.faultCountArm)}
        ${this.createSystemStatusField("Drive", this.warningCountDrive, this.faultCountDrive)}
        ${this.createSystemStatusField("Frame", this.warningCountFrame, this.faultCountFrame)}
      </sh-card>
      <sh-card id='patient-side-software-subsystems-view'>
        ${this.createSystemStatusField("PSRC", this.warningCountPSRC, this.faultCountPSRC)}
        ${this.createSystemStatusField("PSIC", this.warningCountPSIC, this.faultCountPSIC)}
        ${this.createSystemStatusField("PSUI", this.warningCountPSUI, this.faultCountPSUI)}
        ${this.createSystemStatusField("PSSC", this.warningCountPSSC, this.faultCountPSSC)}
      </sh-card>
      <sh-card id='control-side-software-subsystems-view'>
        ${this.createSystemStatusField("CSRC", this.warningCountCSRC, this.faultCountCSRC)}
        ${this.createSystemStatusField("CSIC", this.warningCountCSIC, this.faultCountCSIC)}
        ${this.createSystemStatusField("CSUI", this.warningCountCSUI, this.faultCountCSUI)}
        ${this.createSystemStatusField("CSSC", this.warningCountCSSC, this.faultCountCSSC)}
        ${this.createSystemStatusField("PIM", this.warningCountPIM, this.faultCountPIM)}
      </sh-card>
    </div>
    <sh-button slot='footer' label="Clear Error Count"  size='l' tooltip='left' size='auto' color='secondary' @click='${() => this.emcDisplayFacade.resetFaultsAndWarningsCount()}'></sh-button>
    `;
        this.requestUpdate();
    }
    testOptionTabView() {
        this.activeSection = EmcSection.testOptions;
        this.pageContents = html `
              <div style="display:flex; overflow: hidden">
                <sh-card id='User Input Section'>
                  <sh-card label='Bed Side Control Unit'>
                    <div style="display:flex; flex-direction:column;">
                      <sh-card id='Full-System-EMC-Section' style="padding-top: 12px; margin-top: 8px;">
                        <sh-radio-button label='Full System EMC' ?disabled='${!this.isIdleMonitorRunning || this.isEMCAutomationRunning}' ?active='${this.isIdleMonitorRunning && this.emcFullSystemSelected}' @click='${() => this.enableFullSystemEmc()}'></sh-radio-button>
                      </sh-card>
                      <sh-card id='Sub-System-EMC-Section'>
                        <sh-radio-button label='Sub-System EMC' ?disabled='${!this.isIdleMonitorRunning || this.isEMCAutomationRunning}' ?active='${this.isIdleMonitorRunning && !this.emcFullSystemSelected}' @click='${() => this.enableSubSystemEmc()}'></sh-radio-button>
                          <sh-card id='Sub-System-EMC-Options' label=''>
                          ${[
            { label: 'Move all linear motors', id: 'MoveAllLinearMotors', key: 'moveAllLinearMotors', handler: (id) => this.toggleSubsystem(id) },
            { label: 'Move rotational motor 1', id: 'Mod1RotMotor', key: 'RotJog1', handler: (id) => this.toggleSubsystem(id) },
            { label: 'Move rotational motor 2', id: 'Mod2RotMotor', key: 'RotJog2', handler: (id) => this.toggleSubsystem(id) },
            { label: 'Move rotational motor 3', id: 'Mod3RotMotor', key: 'RotJog3', handler: (id) => this.toggleSubsystem(id) },
            { label: 'Move rotational motor 4', id: 'Mod4RotMotor', key: 'RotJog4', handler: (id) => this.toggleSubsystem(id) },
            { label: 'Unlock and lock arm', id: 'ArmLock', key: 'armLock', handler: (id) => this.toggleSubsystem(id) }
        ].map(item => {
            const key = item.key;
            return html `
                              <sh-checkbox
                                label='${item.label}'
                                id='${item.id}'
                                ?disabled='${!this.isIdleMonitorRunning || this.isEMCAutomationRunning || this.emcFullSystemSelected}'
                                ?active='${this.emcDisplayFacade[key]}'
                                @checked='${() => item.handler(item.id)}'
                              ></sh-checkbox>
                            `;
        })}
                          </sh-card>
                      </sh-card>
                    </div>
                  </sh-card>
                  <sh-card label='User Preferences'>
                    <sh-radio-group horizontal='true'>
                      <sh-radio-button label='Continue Test When System Shows Fault' ?disabled='${this.isEMCAutomationRunning}' ?active='${this.userInput}' @click='${() => this.toggleUserInput('Continue')}'></sh-radio-button>
                      <sh-radio-button label='Stop Test When System Shows Fault' ?disabled='${this.isEMCAutomationRunning}' ?active='${!this.userInput}' @click='${() => this.toggleUserInput('Stop')}'></sh-radio-button>
                    </sh-radio-group>
                  </sh-card>
                </sh-card>
              </div>
    `;
    }
    armSubSystemTabView() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54, _55, _56, _57, _58, _59, _60, _61, _62, _63, _64, _65, _66, _67, _68, _69, _70, _71, _72, _73, _74, _75, _76, _77, _78, _79;
        this.activeSection = EmcSection.armSubSystem;
        let arm = this.arm;
        this.pageContents = html `
    <div class="grid-container system-status-grid">
      <sh-card label='P1P2'>
        <div>
          <div class="drive-inner-content">
            ${this.createField("VMOT (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_b = (_a = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _a === void 0 ? void 0 : _a.vMotor) !== null && _b !== void 0 ? _b : null))}
            ${this.createField("36VIN (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_d = (_c = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _c === void 0 ? void 0 : _c.vMotor36V) !== null && _d !== void 0 ? _d : null))}
            ${this.createField("12V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_f = (_e = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _e === void 0 ? void 0 : _e.vMotor12V) !== null && _f !== void 0 ? _f : null))}
            ${this.createField("5V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_h = (_g = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _g === void 0 ? void 0 : _g.vMotor5V) !== null && _h !== void 0 ? _h : null))}
            ${this.createField("3.3V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_k = (_j = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _j === void 0 ? void 0 : _j.vMotor3p3V) !== null && _k !== void 0 ? _k : null))}
            ${this.createField("P1TempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_m = (_l = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _l === void 0 ? void 0 : _l.M1WTempEst) !== null && _m !== void 0 ? _m : null))}
            ${this.createField("P2TempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_p = (_o = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _o === void 0 ? void 0 : _o.M2WTempEst) !== null && _p !== void 0 ? _p : null))}
            ${this.createField("VA-VB (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_r = (_q = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _q === void 0 ? void 0 : _q.M1VolMeas) !== null && _r !== void 0 ? _r : null))}
            ${this.createField("IA-IB (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_t = (_s = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _s === void 0 ? void 0 : _s.M1Current) !== null && _t !== void 0 ? _t : null))}
            ${this.createField("VC-VD (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_v = (_u = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _u === void 0 ? void 0 : _u.M2VolMeas) !== null && _v !== void 0 ? _v : null))}
            ${this.createField("IC-ID (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_x = (_w = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _w === void 0 ? void 0 : _w.M2Current) !== null && _x !== void 0 ? _x : null))}
            ${this.createField("P1Fault", CorPageEmcTest_1.fixDecimalValueDisplay((_z = (_y = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _y === void 0 ? void 0 : _y.M1ErrorCode) !== null && _z !== void 0 ? _z : null))}
            ${this.createField("P2Fault", CorPageEmcTest_1.fixDecimalValueDisplay((_1 = (_0 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _0 === void 0 ? void 0 : _0.M2ErrorCode) !== null && _1 !== void 0 ? _1 : null))}
            ${this.createField("P1Status", CorPageEmcTest_1.fixDecimalValueDisplay((_3 = (_2 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _2 === void 0 ? void 0 : _2.M1Status) !== null && _3 !== void 0 ? _3 : null))}
            ${this.createField("P2Status", CorPageEmcTest_1.fixDecimalValueDisplay((_5 = (_4 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _4 === void 0 ? void 0 : _4.M2Status) !== null && _5 !== void 0 ? _5 : null))}
            ${this.createField("5V Logic (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_7 = (_6 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _6 === void 0 ? void 0 : _6.vMotor5VLogic) !== null && _7 !== void 0 ? _7 : null))}
            ${this.createField("3V Reference 1 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_9 = (_8 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _8 === void 0 ? void 0 : _8.vMotor3VReference) !== null && _9 !== void 0 ? _9 : null))}
            ${this.createField("3V Reference 2 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_11 = (_10 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _10 === void 0 ? void 0 : _10.vMotor3VSecondReference) !== null && _11 !== void 0 ? _11 : null))}
            ${this.createField("2.5V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_13 = (_12 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _12 === void 0 ? void 0 : _12.vMotor2p5VOffset) !== null && _13 !== void 0 ? _13 : null))}
            ${this.createField("1.2V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_15 = (_14 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _14 === void 0 ? void 0 : _14.vMotor1p2VDspCore) !== null && _15 !== void 0 ? _15 : null))}
          </div>
          <div class="drive-inner-content" style="border-top: 1px solid #eceff3; padding-top: 16px;">
            ${this.createField("P1PosDeg", CorPageEmcTest_1.fixDecimalValueDisplay((_17 = (_16 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _16 === void 0 ? void 0 : _16.M1Pos) !== null && _17 !== void 0 ? _17 : null))}
            ${this.createField("P1VelDegS", CorPageEmcTest_1.fixDecimalValueDisplay((_19 = (_18 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _18 === void 0 ? void 0 : _18.M1Velocity) !== null && _19 !== void 0 ? _19 : null))}
            ${this.createField("P2PosDeg", CorPageEmcTest_1.fixDecimalValueDisplay((_21 = (_20 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _20 === void 0 ? void 0 : _20.M2Pos) !== null && _21 !== void 0 ? _21 : null))}
            ${this.createField("P2VelDegS", CorPageEmcTest_1.fixDecimalValueDisplay((_23 = (_22 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _22 === void 0 ? void 0 : _22.M2Velocity) !== null && _23 !== void 0 ? _23 : null))}
            ${this.createField("P1P2DI", CorPageEmcTest_1.fixDecimalValueDisplay((_25 = (_24 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P1P2) === null || _24 === void 0 ? void 0 : _24.ModuleDI) !== null && _25 !== void 0 ? _25 : null))}
          </div>
        </div>
      </sh-card>
      <sh-card label='P34P5'>
        <div>
          <div class="drive-inner-content">
            ${this.createField("VMOT (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_27 = (_26 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _26 === void 0 ? void 0 : _26.vMotor) !== null && _27 !== void 0 ? _27 : null))}
            ${this.createField("36VIN (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_29 = (_28 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _28 === void 0 ? void 0 : _28.vMotor36V) !== null && _29 !== void 0 ? _29 : null))}
            ${this.createField("12V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_31 = (_30 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _30 === void 0 ? void 0 : _30.vMotor12V) !== null && _31 !== void 0 ? _31 : null))}
            ${this.createField("5V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_33 = (_32 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _32 === void 0 ? void 0 : _32.vMotor5V) !== null && _33 !== void 0 ? _33 : null))}
            ${this.createField("3.3V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_35 = (_34 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _34 === void 0 ? void 0 : _34.vMotor3p3V) !== null && _35 !== void 0 ? _35 : null))}
            ${this.createField("P34TempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_37 = (_36 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _36 === void 0 ? void 0 : _36.M1WTempEst) !== null && _37 !== void 0 ? _37 : null))}
            ${this.createField("P5TempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_39 = (_38 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _38 === void 0 ? void 0 : _38.M2WTempEst) !== null && _39 !== void 0 ? _39 : null))}
            ${this.createField("VA-VB (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_41 = (_40 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _40 === void 0 ? void 0 : _40.M2VolMeas) !== null && _41 !== void 0 ? _41 : null))}
            ${this.createField("IA-IB (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_43 = (_42 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _42 === void 0 ? void 0 : _42.M2Current) !== null && _43 !== void 0 ? _43 : null))}
            ${this.createField("VC-VD (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_45 = (_44 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _44 === void 0 ? void 0 : _44.M1VolMeas) !== null && _45 !== void 0 ? _45 : null))}
            ${this.createField("IC-ID (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_47 = (_46 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _46 === void 0 ? void 0 : _46.M1Current) !== null && _47 !== void 0 ? _47 : null))}
            ${this.createField("P34Fault", CorPageEmcTest_1.fixDecimalValueDisplay((_49 = (_48 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _48 === void 0 ? void 0 : _48.M2ErrorCode) !== null && _49 !== void 0 ? _49 : null))}
            ${this.createField("P5Fault", CorPageEmcTest_1.fixDecimalValueDisplay((_51 = (_50 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _50 === void 0 ? void 0 : _50.M1ErrorCode) !== null && _51 !== void 0 ? _51 : null))}
            ${this.createField("P34Status", CorPageEmcTest_1.fixDecimalValueDisplay((_53 = (_52 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _52 === void 0 ? void 0 : _52.M2Status) !== null && _53 !== void 0 ? _53 : null))}
            ${this.createField("P5Status", CorPageEmcTest_1.fixDecimalValueDisplay((_55 = (_54 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _54 === void 0 ? void 0 : _54.M1Status) !== null && _55 !== void 0 ? _55 : null))}
            ${this.createField("5V Logic (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_57 = (_56 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _56 === void 0 ? void 0 : _56.vMotor5VLogic) !== null && _57 !== void 0 ? _57 : null))}
            ${this.createField("3V Reference 1 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_59 = (_58 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _58 === void 0 ? void 0 : _58.vMotor3VReference) !== null && _59 !== void 0 ? _59 : null))}
            ${this.createField("3V Reference 2 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_61 = (_60 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _60 === void 0 ? void 0 : _60.vMotor3VSecondReference) !== null && _61 !== void 0 ? _61 : null))}
            ${this.createField("2.5V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_63 = (_62 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _62 === void 0 ? void 0 : _62.vMotor2p5VOffset) !== null && _63 !== void 0 ? _63 : null))}
            ${this.createField("1.2V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_65 = (_64 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _64 === void 0 ? void 0 : _64.vMotor1p2VDspCore) !== null && _65 !== void 0 ? _65 : null))}
          </div>
          <div class="drive-inner-content" style="border-top: 1px solid #eceff3; padding-top: 16px;">
            ${this.createField("P34PosDeg", CorPageEmcTest_1.fixDecimalValueDisplay((_67 = (_66 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _66 === void 0 ? void 0 : _66.M2Pos) !== null && _67 !== void 0 ? _67 : null))}
            ${this.createField("P34VelDegS", CorPageEmcTest_1.fixDecimalValueDisplay((_69 = (_68 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _68 === void 0 ? void 0 : _68.M2Velocity) !== null && _69 !== void 0 ? _69 : null))}
            ${this.createField("P5PosDeg", CorPageEmcTest_1.fixDecimalValueDisplay((_71 = (_70 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _70 === void 0 ? void 0 : _70.M1Pos) !== null && _71 !== void 0 ? _71 : null))}
            ${this.createField("P5VelDegS", CorPageEmcTest_1.fixDecimalValueDisplay((_73 = (_72 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _72 === void 0 ? void 0 : _72.M1Velocity) !== null && _73 !== void 0 ? _73 : null))}
            ${this.createField("P34P5DI", CorPageEmcTest_1.fixDecimalValueDisplay((_75 = (_74 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_P34P5) === null || _74 === void 0 ? void 0 : _74.ModuleDI) !== null && _75 !== void 0 ? _75 : null))}
          </div>
        </div>
      </sh-card>
      <sh-card label='Sensor Status'>
        ${this.createField("Arm (Un)Lock via ArmBtn", CorPageEmcTest_1.fixDecimalValueDisplay((_77 = (_76 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_SensorStatus) === null || _76 === void 0 ? void 0 : _76.LockedFromArm) !== null && _77 !== void 0 ? _77 : null))}
        ${this.createField("Arm (Un)Lock via DriveBtn", CorPageEmcTest_1.fixDecimalValueDisplay((_79 = (_78 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_SensorStatus) === null || _78 === void 0 ? void 0 : _78.LockedFromFrame) !== null && _79 !== void 0 ? _79 : null))}
      </sh-card>
    </div>
    `;
        this.requestUpdate();
    }
    driveSubSystemTabView() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54, _55, _56, _57, _58, _59, _60, _61, _62, _63, _64, _65, _66, _67, _68, _69, _70, _71, _72, _73, _74, _75, _76, _77, _78, _79, _80, _81, _82, _83, _84, _85, _86, _87, _88, _89, _90, _91, _92, _93, _94, _95, _96, _97, _98, _99, _100, _101, _102, _103, _104, _105, _106, _107, _108, _109, _110, _111, _112, _113, _114, _115, _116, _117, _118, _119, _120, _121, _122, _123, _124, _125, _126, _127, _128, _129, _130, _131, _132, _133, _134, _135, _136, _137, _138, _139, _140, _141, _142, _143, _144, _145, _146, _147, _148, _149, _150, _151, _152, _153, _154, _155, _156, _157, _158, _159, _160, _161, _162, _163, _164, _165, _166, _167, _168, _169, _170, _171, _172, _173, _174, _175, _176, _177, _178, _179, _180, _181, _182, _183, _184, _185, _186, _187, _188, _189, _190, _191, _192, _193, _194, _195, _196, _197, _198, _199, _200, _201, _202, _203, _204, _205, _206, _207, _208, _209, _210, _211, _212, _213, _214, _215, _216, _217, _218, _219;
        this.activeSection = EmcSection.driveSubSystem;
        let drive = this.drive;
        let arm = this.arm;
        let frame = this.frame;
        this.pageContents = html `
    <div class="grid-container drive-grid">
      <sh-card label='Drive1' class="drive-main-card">
        <div>
          <div class="drive-inner-content">
            ${this.createField("VMOT (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_b = (_a = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _a === void 0 ? void 0 : _a.vMotor) !== null && _b !== void 0 ? _b : null))}
            ${this.createField("36VIN (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_d = (_c = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _c === void 0 ? void 0 : _c.vMotor36V) !== null && _d !== void 0 ? _d : null))}
            ${this.createField("12V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_f = (_e = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _e === void 0 ? void 0 : _e.vMotor12V) !== null && _f !== void 0 ? _f : null))}
            ${this.createField("5V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_h = (_g = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _g === void 0 ? void 0 : _g.vMotor5V) !== null && _h !== void 0 ? _h : null))}
            ${this.createField("3.3V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_k = (_j = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _j === void 0 ? void 0 : _j.vMotor3p3V) !== null && _k !== void 0 ? _k : null))}
            ${this.createField("RotTempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_m = (_l = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _l === void 0 ? void 0 : _l.RotTempEst) !== null && _m !== void 0 ? _m : null))}
            ${this.createField("LinTempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_p = (_o = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _o === void 0 ? void 0 : _o.LinTempEst) !== null && _p !== void 0 ? _p : null))}
            ${this.createField("VA-VB (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_r = (_q = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _q === void 0 ? void 0 : _q.LinVolMeas) !== null && _r !== void 0 ? _r : null))}
            ${this.createField("IA-IB (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_t = (_s = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _s === void 0 ? void 0 : _s.LinCurrent) !== null && _t !== void 0 ? _t : null))}
            ${this.createField("VC-VD (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_v = (_u = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _u === void 0 ? void 0 : _u.RotVolMeas) !== null && _v !== void 0 ? _v : null))}
            ${this.createField("IC-ID (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_x = (_w = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _w === void 0 ? void 0 : _w.RotCurrent) !== null && _x !== void 0 ? _x : null))}
            ${this.createField("M1LFault", CorPageEmcTest_1.fixDecimalValueDisplay((_z = (_y = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _y === void 0 ? void 0 : _y.LinErrorCode) !== null && _z !== void 0 ? _z : null))}
            ${this.createField("M1RFault", CorPageEmcTest_1.fixDecimalValueDisplay((_1 = (_0 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _0 === void 0 ? void 0 : _0.RotErrorCode) !== null && _1 !== void 0 ? _1 : null))}
            ${this.createField("M1LStatus", CorPageEmcTest_1.fixDecimalValueDisplay((_3 = (_2 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _2 === void 0 ? void 0 : _2.LinStatus) !== null && _3 !== void 0 ? _3 : null))}
            ${this.createField("M1RStatus", CorPageEmcTest_1.fixDecimalValueDisplay((_5 = (_4 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _4 === void 0 ? void 0 : _4.RotStatus) !== null && _5 !== void 0 ? _5 : null))}
            ${this.createField("M1RP", CorPageEmcTest_1.fixDecimalValueDisplay((_7 = (_6 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _6 === void 0 ? void 0 : _6.RotPos) !== null && _7 !== void 0 ? _7 : null))}
            ${this.createField("S1LP", CorPageEmcTest_1.fixDecimalValueDisplay((_9 = (_8 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _8 === void 0 ? void 0 : _8.LinEncPos) !== null && _9 !== void 0 ? _9 : null))}
            ${this.createField("M1LV", CorPageEmcTest_1.fixDecimalValueDisplay((_11 = (_10 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _10 === void 0 ? void 0 : _10.LinVel) !== null && _11 !== void 0 ? _11 : null))}
            ${this.createField("M1DI", CorPageEmcTest_1.fixDecimalValueDisplay((_13 = (_12 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _12 === void 0 ? void 0 : _12.ModuleDI) !== null && _13 !== void 0 ? _13 : null))}
            ${this.createField("5V Logic (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_15 = (_14 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _14 === void 0 ? void 0 : _14.vMotor5VLogic) !== null && _15 !== void 0 ? _15 : null))}
            ${this.createField("3V Reference 1 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_17 = (_16 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _16 === void 0 ? void 0 : _16.vMotor3VReference) !== null && _17 !== void 0 ? _17 : null))}
            ${this.createField("3V Reference 2 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_19 = (_18 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _18 === void 0 ? void 0 : _18.vMotor3VSecondReference) !== null && _19 !== void 0 ? _19 : null))}
            ${this.createField("2.5V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_21 = (_20 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _20 === void 0 ? void 0 : _20.vMotor2p5VOffset) !== null && _21 !== void 0 ? _21 : null))}
            ${this.createField("1.2V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_23 = (_22 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _22 === void 0 ? void 0 : _22.vMotor1p2VDspCore) !== null && _23 !== void 0 ? _23 : null))}
            ${this.createField("LEV Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_25 = (_24 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive1) === null || _24 === void 0 ? void 0 : _24.vMotorLEVSensedSupplyVoltage) !== null && _25 !== void 0 ? _25 : null))}
          </div>
          <div class="drive-inner-content" style="border-top: 1px solid #eceff3; padding-top: 16px;">
            <div style="font-weight: bold; margin-bottom: 8px; font-size: 16px;">Sensor Status Drive 1</div>
            ${this.createField("Cassette OpenClosed", CorPageEmcTest_1.fixDecimalValueDisplay((_27 = (_26 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive1) === null || _26 === void 0 ? void 0 : _26.CasetteOpenClose) !== null && _27 !== void 0 ? _27 : null))}
            ${this.createField("Cassette LockedUnlocked", CorPageEmcTest_1.fixDecimalValueDisplay((_29 = (_28 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive1) === null || _28 === void 0 ? void 0 : _28.CasetteLockedUnlocked) !== null && _29 !== void 0 ? _29 : null))}
            ${this.createField("Cassette Tube", CorPageEmcTest_1.fixDecimalValueDisplay((_31 = (_30 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive1) === null || _30 === void 0 ? void 0 : _30.CasetteTube) !== null && _31 !== void 0 ? _31 : null))}
            ${this.createField("ESTOP", CorPageEmcTest_1.fixDecimalValueDisplay((_33 = (_32 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive1) === null || _32 === void 0 ? void 0 : _32.Estop) !== null && _33 !== void 0 ? _33 : null))}
          </div>
        </div>
      </sh-card>
      <sh-card label='Drive2' class="drive-main-card">
        <div>
        <div class="drive-inner-content">
            ${this.createField("VMOT (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_35 = (_34 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _34 === void 0 ? void 0 : _34.vMotor) !== null && _35 !== void 0 ? _35 : null))}
            ${this.createField("36VIN (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_37 = (_36 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _36 === void 0 ? void 0 : _36.vMotor36V) !== null && _37 !== void 0 ? _37 : null))}
            ${this.createField("12V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_39 = (_38 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _38 === void 0 ? void 0 : _38.vMotor12V) !== null && _39 !== void 0 ? _39 : null))}
            ${this.createField("5V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_41 = (_40 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _40 === void 0 ? void 0 : _40.vMotor5V) !== null && _41 !== void 0 ? _41 : null))}
            ${this.createField("3.3V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_43 = (_42 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _42 === void 0 ? void 0 : _42.vMotor3p3V) !== null && _43 !== void 0 ? _43 : null))}
            ${this.createField("RotTempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_45 = (_44 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _44 === void 0 ? void 0 : _44.RotTempEst) !== null && _45 !== void 0 ? _45 : null))}
            ${this.createField("LinTempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_47 = (_46 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _46 === void 0 ? void 0 : _46.LinTempEst) !== null && _47 !== void 0 ? _47 : null))}
            ${this.createField("VA-VB (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_49 = (_48 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _48 === void 0 ? void 0 : _48.LinVolMeas) !== null && _49 !== void 0 ? _49 : null))}
            ${this.createField("IA-IB (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_51 = (_50 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _50 === void 0 ? void 0 : _50.LinCurrent) !== null && _51 !== void 0 ? _51 : null))}
            ${this.createField("VC-VD (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_53 = (_52 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _52 === void 0 ? void 0 : _52.RotVolMeas) !== null && _53 !== void 0 ? _53 : null))}
            ${this.createField("IC-ID (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_55 = (_54 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _54 === void 0 ? void 0 : _54.RotCurrent) !== null && _55 !== void 0 ? _55 : null))}
            ${this.createField("M2LFault", CorPageEmcTest_1.fixDecimalValueDisplay((_57 = (_56 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _56 === void 0 ? void 0 : _56.LinErrorCode) !== null && _57 !== void 0 ? _57 : null))}
            ${this.createField("M2RFault", CorPageEmcTest_1.fixDecimalValueDisplay((_59 = (_58 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _58 === void 0 ? void 0 : _58.RotErrorCode) !== null && _59 !== void 0 ? _59 : null))}
            ${this.createField("M2LStatus", CorPageEmcTest_1.fixDecimalValueDisplay((_61 = (_60 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _60 === void 0 ? void 0 : _60.LinStatus) !== null && _61 !== void 0 ? _61 : null))}
            ${this.createField("M2RStatus", CorPageEmcTest_1.fixDecimalValueDisplay((_63 = (_62 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _62 === void 0 ? void 0 : _62.RotStatus) !== null && _63 !== void 0 ? _63 : null))}
            ${this.createField("M2RP", CorPageEmcTest_1.fixDecimalValueDisplay((_65 = (_64 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _64 === void 0 ? void 0 : _64.RotPos) !== null && _65 !== void 0 ? _65 : null))}
            ${this.createField("S2LP", CorPageEmcTest_1.fixDecimalValueDisplay((_67 = (_66 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _66 === void 0 ? void 0 : _66.LinEncPos) !== null && _67 !== void 0 ? _67 : null))}
            ${this.createField("M2LV", CorPageEmcTest_1.fixDecimalValueDisplay((_69 = (_68 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _68 === void 0 ? void 0 : _68.LinVel) !== null && _69 !== void 0 ? _69 : null))}
            ${this.createField("M2DI", CorPageEmcTest_1.fixDecimalValueDisplay((_71 = (_70 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _70 === void 0 ? void 0 : _70.ModuleDI) !== null && _71 !== void 0 ? _71 : null))}
            ${this.createField("5V Logic (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_73 = (_72 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _72 === void 0 ? void 0 : _72.vMotor5VLogic) !== null && _73 !== void 0 ? _73 : null))}
            ${this.createField("3V Reference 1 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_75 = (_74 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _74 === void 0 ? void 0 : _74.vMotor3VReference) !== null && _75 !== void 0 ? _75 : null))}
            ${this.createField("3V Reference 2 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_77 = (_76 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _76 === void 0 ? void 0 : _76.vMotor3VSecondReference) !== null && _77 !== void 0 ? _77 : null))}
            ${this.createField("2.5V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_79 = (_78 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _78 === void 0 ? void 0 : _78.vMotor2p5VOffset) !== null && _79 !== void 0 ? _79 : null))}
            ${this.createField("1.2V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_81 = (_80 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _80 === void 0 ? void 0 : _80.vMotor1p2VDspCore) !== null && _81 !== void 0 ? _81 : null))}
            ${this.createField("LEV Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_83 = (_82 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive2) === null || _82 === void 0 ? void 0 : _82.vMotorLEVSensedSupplyVoltage) !== null && _83 !== void 0 ? _83 : null))}
          </div>
          <div class="drive-inner-content" style="border-top: 1px solid #eceff3; padding-top: 16px;">
            <div style="font-weight: bold; margin-bottom: 8px; font-size: 16px;">Sensor Status Drive 2</div>
            ${this.createField("Cassette OpenClosed", CorPageEmcTest_1.fixDecimalValueDisplay((_85 = (_84 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive2) === null || _84 === void 0 ? void 0 : _84.CasetteOpenClose) !== null && _85 !== void 0 ? _85 : null))}
            ${this.createField("Cassette LockedUnlocked", CorPageEmcTest_1.fixDecimalValueDisplay((_87 = (_86 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive2) === null || _86 === void 0 ? void 0 : _86.CasetteLockedUnlocked) !== null && _87 !== void 0 ? _87 : null))}
            ${this.createField("Cassette Tube", CorPageEmcTest_1.fixDecimalValueDisplay((_89 = (_88 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive2) === null || _88 === void 0 ? void 0 : _88.CasetteTube) !== null && _89 !== void 0 ? _89 : null))}
            ${this.createField("ESTOP", CorPageEmcTest_1.fixDecimalValueDisplay((_91 = (_90 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive2) === null || _90 === void 0 ? void 0 : _90.Estop) !== null && _91 !== void 0 ? _91 : null))}
          </div>
        </div>
      </sh-card>

      <sh-card label='Drive3' class="drive-main-card">
        <div>
          <div class="drive-inner-content">

            ${this.createField("VMOT (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_93 = (_92 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _92 === void 0 ? void 0 : _92.vMotor) !== null && _93 !== void 0 ? _93 : null))}
            ${this.createField("36VIN (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_95 = (_94 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _94 === void 0 ? void 0 : _94.vMotor36V) !== null && _95 !== void 0 ? _95 : null))}
            ${this.createField("12V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_97 = (_96 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _96 === void 0 ? void 0 : _96.vMotor12V) !== null && _97 !== void 0 ? _97 : null))}
            ${this.createField("5V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_99 = (_98 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _98 === void 0 ? void 0 : _98.vMotor5V) !== null && _99 !== void 0 ? _99 : null))}
            ${this.createField("3.3V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_101 = (_100 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _100 === void 0 ? void 0 : _100.vMotor3p3V) !== null && _101 !== void 0 ? _101 : null))}
            ${this.createField("RotTempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_103 = (_102 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _102 === void 0 ? void 0 : _102.RotTempEst) !== null && _103 !== void 0 ? _103 : null))}
            ${this.createField("LinTempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_105 = (_104 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _104 === void 0 ? void 0 : _104.LinTempEst) !== null && _105 !== void 0 ? _105 : null))}
            ${this.createField("VA-VB (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_107 = (_106 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _106 === void 0 ? void 0 : _106.LinVolMeas) !== null && _107 !== void 0 ? _107 : null))}
            ${this.createField("IA-IB (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_109 = (_108 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _108 === void 0 ? void 0 : _108.LinCurrent) !== null && _109 !== void 0 ? _109 : null))}
            ${this.createField("VC-VD (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_111 = (_110 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _110 === void 0 ? void 0 : _110.RotVolMeas) !== null && _111 !== void 0 ? _111 : null))}
            ${this.createField("IC-ID (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_113 = (_112 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _112 === void 0 ? void 0 : _112.RotCurrent) !== null && _113 !== void 0 ? _113 : null))}
            ${this.createField("M3LFault", CorPageEmcTest_1.fixDecimalValueDisplay((_115 = (_114 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _114 === void 0 ? void 0 : _114.LinErrorCode) !== null && _115 !== void 0 ? _115 : null))}
            ${this.createField("M3RFault", CorPageEmcTest_1.fixDecimalValueDisplay((_117 = (_116 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _116 === void 0 ? void 0 : _116.RotErrorCode) !== null && _117 !== void 0 ? _117 : null))}
            ${this.createField("M3LStatus", CorPageEmcTest_1.fixDecimalValueDisplay((_119 = (_118 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _118 === void 0 ? void 0 : _118.LinStatus) !== null && _119 !== void 0 ? _119 : null))}
            ${this.createField("M3RStatus", CorPageEmcTest_1.fixDecimalValueDisplay((_121 = (_120 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _120 === void 0 ? void 0 : _120.RotStatus) !== null && _121 !== void 0 ? _121 : null))}
            ${this.createField("M3RP", CorPageEmcTest_1.fixDecimalValueDisplay((_123 = (_122 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _122 === void 0 ? void 0 : _122.RotPos) !== null && _123 !== void 0 ? _123 : null))}
            ${this.createField("S3LP", CorPageEmcTest_1.fixDecimalValueDisplay((_125 = (_124 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _124 === void 0 ? void 0 : _124.LinEncPos) !== null && _125 !== void 0 ? _125 : null))}
            ${this.createField("M3LV", CorPageEmcTest_1.fixDecimalValueDisplay((_127 = (_126 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _126 === void 0 ? void 0 : _126.LinVel) !== null && _127 !== void 0 ? _127 : null))}
            ${this.createField("M3DI", CorPageEmcTest_1.fixDecimalValueDisplay((_129 = (_128 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _128 === void 0 ? void 0 : _128.ModuleDI) !== null && _129 !== void 0 ? _129 : null))}
            ${this.createField("5V Logic (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_131 = (_130 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _130 === void 0 ? void 0 : _130.vMotor5VLogic) !== null && _131 !== void 0 ? _131 : null))}
            ${this.createField("3V Reference 1 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_133 = (_132 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _132 === void 0 ? void 0 : _132.vMotor3VReference) !== null && _133 !== void 0 ? _133 : null))}
            ${this.createField("3V Reference 2 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_135 = (_134 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _134 === void 0 ? void 0 : _134.vMotor3VSecondReference) !== null && _135 !== void 0 ? _135 : null))}
            ${this.createField("2.5V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_137 = (_136 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _136 === void 0 ? void 0 : _136.vMotor2p5VOffset) !== null && _137 !== void 0 ? _137 : null))}
            ${this.createField("1.2V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_139 = (_138 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _138 === void 0 ? void 0 : _138.vMotor1p2VDspCore) !== null && _139 !== void 0 ? _139 : null))}
            ${this.createField("LEV Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_141 = (_140 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive3) === null || _140 === void 0 ? void 0 : _140.vMotorLEVSensedSupplyVoltage) !== null && _141 !== void 0 ? _141 : null))}
          </div>
          <div class="drive-inner-content" style="border-top: 1px solid #eceff3; padding-top: 16px;">
            <div style="font-weight: bold; margin-bottom: 8px; font-size: 16px;">Sensor Status Drive 3</div>
            ${this.createField("Casette OpenClosed", CorPageEmcTest_1.fixDecimalValueDisplay((_143 = (_142 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive3) === null || _142 === void 0 ? void 0 : _142.CasetteOpenClose) !== null && _143 !== void 0 ? _143 : null))}
            ${this.createField("Cassette LockedUnlocked", CorPageEmcTest_1.fixDecimalValueDisplay((_145 = (_144 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive3) === null || _144 === void 0 ? void 0 : _144.CasetteLockedUnlocked) !== null && _145 !== void 0 ? _145 : null))}
            ${this.createField("Cassette Tube", CorPageEmcTest_1.fixDecimalValueDisplay((_147 = (_146 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive3) === null || _146 === void 0 ? void 0 : _146.CasetteTube) !== null && _147 !== void 0 ? _147 : null))}
            ${this.createField("ESTOP", CorPageEmcTest_1.fixDecimalValueDisplay((_149 = (_148 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive3) === null || _148 === void 0 ? void 0 : _148.Estop) !== null && _149 !== void 0 ? _149 : null))}
          </div>
        </div>
      </sh-card>

      <sh-card label='Drive4' class="drive-main-card">
        <div>
        <div class="drive-inner-content">
            ${this.createField("VMOT (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_151 = (_150 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _150 === void 0 ? void 0 : _150.vMotor) !== null && _151 !== void 0 ? _151 : null))}
            ${this.createField("36VIN (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_153 = (_152 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _152 === void 0 ? void 0 : _152.vMotor36V) !== null && _153 !== void 0 ? _153 : null))}
            ${this.createField("12V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_155 = (_154 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _154 === void 0 ? void 0 : _154.vMotor12V) !== null && _155 !== void 0 ? _155 : null))}
            ${this.createField("5V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_157 = (_156 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _156 === void 0 ? void 0 : _156.vMotor5V) !== null && _157 !== void 0 ? _157 : null))}
            ${this.createField("3.3V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_159 = (_158 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _158 === void 0 ? void 0 : _158.vMotor3p3V) !== null && _159 !== void 0 ? _159 : null))}
            ${this.createField("RotTempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_161 = (_160 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _160 === void 0 ? void 0 : _160.RotTempEst) !== null && _161 !== void 0 ? _161 : null))}
            ${this.createField("LinTempEst (*C)", CorPageEmcTest_1.fixDecimalValueDisplay((_163 = (_162 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _162 === void 0 ? void 0 : _162.LinTempEst) !== null && _163 !== void 0 ? _163 : null))}
            ${this.createField("VA-VB (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_165 = (_164 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _164 === void 0 ? void 0 : _164.LinVolMeas) !== null && _165 !== void 0 ? _165 : null))}
            ${this.createField("IA-IB (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_167 = (_166 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _166 === void 0 ? void 0 : _166.LinCurrent) !== null && _167 !== void 0 ? _167 : null))}
            ${this.createField("VC-VD (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_169 = (_168 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _168 === void 0 ? void 0 : _168.RotVolMeas) !== null && _169 !== void 0 ? _169 : null))}
            ${this.createField("IC-ID (A)", CorPageEmcTest_1.fixDecimalValueDisplay((_171 = (_170 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _170 === void 0 ? void 0 : _170.RotCurrent) !== null && _171 !== void 0 ? _171 : null))}
            ${this.createField("M4LFault", CorPageEmcTest_1.fixDecimalValueDisplay((_173 = (_172 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _172 === void 0 ? void 0 : _172.LinErrorCode) !== null && _173 !== void 0 ? _173 : null))}
            ${this.createField("M4RFault", CorPageEmcTest_1.fixDecimalValueDisplay((_175 = (_174 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _174 === void 0 ? void 0 : _174.RotErrorCode) !== null && _175 !== void 0 ? _175 : null))}
            ${this.createField("M4LStatus", CorPageEmcTest_1.fixDecimalValueDisplay((_177 = (_176 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _176 === void 0 ? void 0 : _176.LinStatus) !== null && _177 !== void 0 ? _177 : null))}
            ${this.createField("M4RStatus", CorPageEmcTest_1.fixDecimalValueDisplay((_179 = (_178 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _178 === void 0 ? void 0 : _178.RotStatus) !== null && _179 !== void 0 ? _179 : null))}
            ${this.createField("M4RP", CorPageEmcTest_1.fixDecimalValueDisplay((_181 = (_180 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _180 === void 0 ? void 0 : _180.RotPos) !== null && _181 !== void 0 ? _181 : null))}
            ${this.createField("S4LP", CorPageEmcTest_1.fixDecimalValueDisplay((_183 = (_182 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _182 === void 0 ? void 0 : _182.LinEncPos) !== null && _183 !== void 0 ? _183 : null))}
            ${this.createField("M4LV", CorPageEmcTest_1.fixDecimalValueDisplay((_185 = (_184 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _184 === void 0 ? void 0 : _184.LinVel) !== null && _185 !== void 0 ? _185 : null))}
            ${this.createField("M4DI", CorPageEmcTest_1.fixDecimalValueDisplay((_187 = (_186 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _186 === void 0 ? void 0 : _186.ModuleDI) !== null && _187 !== void 0 ? _187 : null))}
            ${this.createField("5V Logic (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_189 = (_188 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _188 === void 0 ? void 0 : _188.vMotor5VLogic) !== null && _189 !== void 0 ? _189 : null))}
            ${this.createField("3V Reference 1 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_191 = (_190 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _190 === void 0 ? void 0 : _190.vMotor3VReference) !== null && _191 !== void 0 ? _191 : null))}
            ${this.createField("3V Reference 2 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_193 = (_192 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _192 === void 0 ? void 0 : _192.vMotor3VSecondReference) !== null && _193 !== void 0 ? _193 : null))}
            ${this.createField("2.5V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_195 = (_194 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _194 === void 0 ? void 0 : _194.vMotor2p5VOffset) !== null && _195 !== void 0 ? _195 : null))}
            ${this.createField("1.2V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_197 = (_196 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _196 === void 0 ? void 0 : _196.vMotor1p2VDspCore) !== null && _197 !== void 0 ? _197 : null))}
            ${this.createField("LEV Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_199 = (_198 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_Drive4) === null || _198 === void 0 ? void 0 : _198.vMotorLEVSensedSupplyVoltage) !== null && _199 !== void 0 ? _199 : null))}
          </div>
          <div class="drive-inner-content" style="border-top: 1px solid #eceff3; padding-top: 16px;">
            <div style="font-weight: bold; margin-bottom: 8px; font-size: 16px;">Sensor Status Drive 4</div>
            ${this.createField("Cassette OpenClosed", CorPageEmcTest_1.fixDecimalValueDisplay((_201 = (_200 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive4) === null || _200 === void 0 ? void 0 : _200.CasetteOpenClose) !== null && _201 !== void 0 ? _201 : null))}
            ${this.createField("Cassette LockedUnlocked", CorPageEmcTest_1.fixDecimalValueDisplay((_203 = (_202 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive4) === null || _202 === void 0 ? void 0 : _202.CasetteLockedUnlocked) !== null && _203 !== void 0 ? _203 : null))}
            ${this.createField("ESTOP", CorPageEmcTest_1.fixDecimalValueDisplay((_205 = (_204 = drive === null || drive === void 0 ? void 0 : drive.p_Drive_SensorStatusDrive4) === null || _204 === void 0 ? void 0 : _204.Estop) !== null && _205 !== void 0 ? _205 : null))}
          </div>
        </div>
      </sh-card>

      <sh-card label='Frame' class="drive-main-card" style="padding: 0px 0px;">
        <div>
          <div class="drive-inner-content">
            ${this.createField("36VIN (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_206 = frame === null || frame === void 0 ? void 0 : frame["36V"]) !== null && _206 !== void 0 ? _206 : null))}
            ${this.createField("5V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_207 = frame === null || frame === void 0 ? void 0 : frame["5V"]) !== null && _207 !== void 0 ? _207 : null))}
            ${this.createField("3.3V (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_208 = frame === null || frame === void 0 ? void 0 : frame["3V3"]) !== null && _208 !== void 0 ? _208 : null))}
            ${this.createField("Temperature", CorPageEmcTest_1.fixDecimalValueDisplay((_209 = frame === null || frame === void 0 ? void 0 : frame.FrameTemp) !== null && _209 !== void 0 ? _209 : null))}
            ${this.createField("12V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_210 = frame === null || frame === void 0 ? void 0 : frame["12V"]) !== null && _210 !== void 0 ? _210 : null))}
            ${this.createField("5V Logic (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_211 = frame === null || frame === void 0 ? void 0 : frame["5VLogic"]) !== null && _211 !== void 0 ? _211 : null))}
            ${this.createField("3V Reference 1 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_212 = frame === null || frame === void 0 ? void 0 : frame["3VReference"]) !== null && _212 !== void 0 ? _212 : null))}
            ${this.createField("3V Reference 2 (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_213 = frame === null || frame === void 0 ? void 0 : frame["3V2ndReference"]) !== null && _213 !== void 0 ? _213 : null))}
            ${this.createField("2.5V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_214 = frame === null || frame === void 0 ? void 0 : frame["2V5Offset"]) !== null && _214 !== void 0 ? _214 : null))}
            ${this.createField("1.2V Sense (V)", CorPageEmcTest_1.fixDecimalValueDisplay((_215 = frame === null || frame === void 0 ? void 0 : frame["1V2DspCore"]) !== null && _215 !== void 0 ? _215 : null))}
            ${this.createField("Proximal Detect", CorPageEmcTest_1.fixDecimalValueDisplay((_217 = (_216 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_SensorStatus) === null || _216 === void 0 ? void 0 : _216.ProximalAnchor) !== null && _217 !== void 0 ? _217 : null))}
            ${this.createField("Distal Define Detect", CorPageEmcTest_1.fixDecimalValueDisplay((_219 = (_218 = arm === null || arm === void 0 ? void 0 : arm.p_Arm_SensorStatus) === null || _218 === void 0 ? void 0 : _218.DistalAnchor) !== null && _219 !== void 0 ? _219 : null))}
          </div>
        </div>
      </sh-card>
    </div>
    `;
        this.requestUpdate();
    }
    psPdoSubSystemTabView() {
        this.activeSection = EmcSection.psPdoSubSystem;
        let pdoData = this.psPdo;
        this.pdoTabView(pdoData);
        this.requestUpdate();
    }
    csPdoSubSystemTabView() {
        this.activeSection = EmcSection.csPdoSubSystem;
        let pdoData = this.csPdo;
        this.pdoTabView(pdoData);
        this.requestUpdate();
    }
    pdoTabView(pdoData) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        let latencyData = this.latency;
        let control_connection_status = this.controlConnectionStatus;
        let communication_fault_count = this.communicationFaultCount;
        this.pageContents = html `
      <div style="display:flex; flex-direction:column; gap:16px;">

        <sh-card label="CPU PDO Data">
          ${this.createField("IC CPU Usage", CorPageEmcTest_1.fixDecimalValueDisplay((_b = (_a = pdoData === null || pdoData === void 0 ? void 0 : pdoData.ic) === null || _a === void 0 ? void 0 : _a.cpuUsage) !== null && _b !== void 0 ? _b : null))}
          ${this.createField("IC Memory", CorPageEmcTest_1.fixDecimalValueDisplay((_d = (_c = pdoData === null || pdoData === void 0 ? void 0 : pdoData.ic) === null || _c === void 0 ? void 0 : _c.memoryUsage) !== null && _d !== void 0 ? _d : null))}
          ${this.createField("SC CPU Usage", CorPageEmcTest_1.fixDecimalValueDisplay((_f = (_e = pdoData === null || pdoData === void 0 ? void 0 : pdoData.sc) === null || _e === void 0 ? void 0 : _e.cpuUsage) !== null && _f !== void 0 ? _f : null))}
          ${this.createField("SC Memory", CorPageEmcTest_1.fixDecimalValueDisplay((_h = (_g = pdoData === null || pdoData === void 0 ? void 0 : pdoData.sc) === null || _g === void 0 ? void 0 : _g.memoryUsage) !== null && _h !== void 0 ? _h : null))}
          ${this.createField("RC CPU Usage", CorPageEmcTest_1.fixDecimalValueDisplay((_k = (_j = pdoData === null || pdoData === void 0 ? void 0 : pdoData.rc) === null || _j === void 0 ? void 0 : _j.cpuUsage) !== null && _k !== void 0 ? _k : null))}
          ${this.createField("RC Memory", CorPageEmcTest_1.fixDecimalValueDisplay((_m = (_l = pdoData === null || pdoData === void 0 ? void 0 : pdoData.rc) === null || _l === void 0 ? void 0 : _l.memoryUsage) !== null && _m !== void 0 ? _m : null))}
          ${this.createField("UI CPU Usage", CorPageEmcTest_1.fixDecimalValueDisplay((_p = (_o = pdoData === null || pdoData === void 0 ? void 0 : pdoData.ui) === null || _o === void 0 ? void 0 : _o.cpuUsage) !== null && _p !== void 0 ? _p : null))}
          ${this.createField("UI Memory", CorPageEmcTest_1.fixDecimalValueDisplay((_r = (_q = pdoData === null || pdoData === void 0 ? void 0 : pdoData.ui) === null || _q === void 0 ? void 0 : _q.memoryUsage) !== null && _r !== void 0 ? _r : null))}
        </sh-card>

        <sh-card label="System Latency">
          ${this.createField("Latency", latencyData !== null && latencyData !== void 0 ? latencyData : EmcConsts.NOT_AVAILABLE)}
        </sh-card>

        <sh-card label="Control Connection Status">
          ${this.createField("Control Connection Status", control_connection_status || EmcConsts.NOT_AVAILABLE)}
        </sh-card>

        <sh-card label="Communication Fault Count">
          ${this.createField("Communication Fault Count", communication_fault_count)}
        </sh-card>

      </div>
    `;
    }
    patientSoftwareVersionTabView() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1;
        this.activeSection = EmcSection.patientSoftwareVersion;
        let version = this.patientSubsystemsVersionData;
        this.pageContents = html `
    <div style="display:flex; overflow: hidden">
      <sh-card>
        ${this.createSoftwareVersionField("Drive Module 1", (_b = (_a = version === null || version === void 0 ? void 0 : version.drive1_version) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : null)}
        ${this.createSoftwareVersionField("Drive Module 2", (_d = (_c = version === null || version === void 0 ? void 0 : version.drive2_version) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : null)}
        ${this.createSoftwareVersionField("Drive Module 3", (_f = (_e = version === null || version === void 0 ? void 0 : version.drive3_version) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : null)}
        ${this.createSoftwareVersionField("Drive Module 4", (_h = (_g = version === null || version === void 0 ? void 0 : version.drive4_version) === null || _g === void 0 ? void 0 : _g.toString()) !== null && _h !== void 0 ? _h : null)}
        ${this.createSoftwareVersionField("Arm P1P2", (_k = (_j = version === null || version === void 0 ? void 0 : version.p1p2_version) === null || _j === void 0 ? void 0 : _j.toString()) !== null && _k !== void 0 ? _k : null)}
      </sh-card>
      <sh-card>
        ${this.createSoftwareVersionField("Arm P34P5", (_m = (_l = version === null || version === void 0 ? void 0 : version.p34p5_version) === null || _l === void 0 ? void 0 : _l.toString()) !== null && _m !== void 0 ? _m : null)}
        ${this.createSoftwareVersionField("Frameboard", (_p = (_o = version === null || version === void 0 ? void 0 : version.frame_version) === null || _o === void 0 ? void 0 : _o.toString()) !== null && _p !== void 0 ? _p : null)}
        ${this.createSoftwareVersionField("PSRC Version", (_r = (_q = version === null || version === void 0 ? void 0 : version.psrc_version) === null || _q === void 0 ? void 0 : _q.toString()) !== null && _r !== void 0 ? _r : null)}
        ${this.createSoftwareVersionField("PSIC Version", (_t = (_s = version === null || version === void 0 ? void 0 : version.psic_version) === null || _s === void 0 ? void 0 : _s.toString()) !== null && _t !== void 0 ? _t : null)}
        ${this.createSoftwareVersionField("PSUI Version", (_v = (_u = version === null || version === void 0 ? void 0 : version.psui_version) === null || _u === void 0 ? void 0 : _u.toString()) !== null && _v !== void 0 ? _v : null)}
      </sh-card>
      <sh-card>
        ${this.createSoftwareVersionField("PSSC Version", (_x = (_w = version === null || version === void 0 ? void 0 : version.pssc_version) === null || _w === void 0 ? void 0 : _w.toString()) !== null && _x !== void 0 ? _x : null)}
        ${this.createSoftwareVersionField("EMC Software Version", (_z = (_y = version === null || version === void 0 ? void 0 : version.emc_software_version) === null || _y === void 0 ? void 0 : _y.toString()) !== null && _z !== void 0 ? _z : null)}
        ${this.createSoftwareVersionField("EMC UI Tool Version", (_1 = (_0 = version === null || version === void 0 ? void 0 : version.emc_ui_tool_version) === null || _0 === void 0 ? void 0 : _0.toString()) !== null && _1 !== void 0 ? _1 : null)}
      </sh-card>
    </div>
    `;
        this.requestUpdate();
    }
    controlSoftwareVersionTabView() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        this.activeSection = EmcSection.controlSoftwareVersion;
        let version = this.controlSubsystemsVersionData;
        this.pageContents = html `
    <div style="display:flex; overflow: hidden">
      <sh-card>
        ${this.createSoftwareVersionField("CSRC Version", (_b = (_a = version === null || version === void 0 ? void 0 : version.csrc_version) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : null)}
        ${this.createSoftwareVersionField("CSIC Version", (_d = (_c = version === null || version === void 0 ? void 0 : version.csic_version) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : null)}
        ${this.createSoftwareVersionField("CSUI Version", (_f = (_e = version === null || version === void 0 ? void 0 : version.csui_version) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : null)}
        ${this.createSoftwareVersionField("CSSC Version", (_h = (_g = version === null || version === void 0 ? void 0 : version.cssc_version) === null || _g === void 0 ? void 0 : _g.toString()) !== null && _h !== void 0 ? _h : null)}
        ${this.createSoftwareVersionField("PIM Version", (_k = (_j = version === null || version === void 0 ? void 0 : version.pim_version) === null || _j === void 0 ? void 0 : _j.toString()) !== null && _k !== void 0 ? _k : null)}
      </sh-card>
      <sh-card>
      </sh-card>
      <sh-card>
      </sh-card>
    </div>
    `;
        this.requestUpdate();
    }
    pimDataTabView() {
        var _a, _b, _c, _d, _e, _f;
        this.activeSection = EmcSection.pimData;
        let pim = this.pim;
        this.pageContents = html `
    <div style="display: flex; overflow: hidden;">
      <sh-card label = 'Primary Input Module' id = "pim" style = "width: 35%;">
        ${this.createField("Digital 5V", CorPageEmcTest_1.fixDecimalValueDisplay((_a = pim === null || pim === void 0 ? void 0 : pim.digital5V) !== null && _a !== void 0 ? _a : null))}
        ${this.createField("Digital 3.3V", CorPageEmcTest_1.fixDecimalValueDisplay((_b = pim === null || pim === void 0 ? void 0 : pim.digital3V) !== null && _b !== void 0 ? _b : null))}
        ${this.createField("5VCurrent", CorPageEmcTest_1.fixDecimalValueDisplay((_c = pim === null || pim === void 0 ? void 0 : pim.X5VCurrent) !== null && _c !== void 0 ? _c : null))}
        ${this.createField("3VCurrent", CorPageEmcTest_1.fixDecimalValueDisplay((_d = pim === null || pim === void 0 ? void 0 : pim.X3VCurrent) !== null && _d !== void 0 ? _d : null))}
        ${this.createField("LED Current", CorPageEmcTest_1.fixDecimalValueDisplay((_e = pim === null || pim === void 0 ? void 0 : pim.ledCurrent) !== null && _e !== void 0 ? _e : null))}
        ${this.createField("State Update Interval", CorPageEmcTest_1.fixDecimalValueDisplay((_f = pim === null || pim === void 0 ? void 0 : pim.SUI) !== null && _f !== void 0 ? _f : null))}
      </sh-card>
    </div>
    `;
        this.requestUpdate();
    }
    findValue(key, subsystem) {
        var _a, _b;
        let decimalValue;
        if (subsystem == "CSRC")
            decimalValue = CorPageEmcTest_1.fixDecimalValueDisplay((_a = this.csrc) === null || _a === void 0 ? void 0 : _a.fieldMap.get(key));
        else if (subsystem == "PSRC")
            decimalValue = CorPageEmcTest_1.fixDecimalValueDisplay((_b = this.psrc) === null || _b === void 0 ? void 0 : _b.fieldMap.get(key));
        return decimalValue;
    }
    RcDataTabView() {
        this.activeSection = EmcSection.RcData;
        const fields = [
            { label: "RC 12 Vd Rail", key: "RcDigital12Vdc" },
            { label: "RC 5V Rail", key: "RcDigital5VSense" },
            { label: "RC 3.3 Vdc Rail", key: "RcDigital3Vdc" },
            { label: "RC 3.3 Vdc Analog", key: "RcDigital3VdcAnalog" },
            { label: "RC 3.3 Vdc SW Rail", key: "RcDigital3VdcSW" },
            { label: "CPU Usage", key: "RcOsMonitorCpu" },
            { label: "Memory Availability", key: "RcOsMonitorRam" },
        ];
        this.pageContents = html `
    <div style="display:flex; overflow: hidden">
      <sh-card label='PSRC Data' id = "psrc" style = "width: 35%;">
      ${fields.map((field) => this.createField(field.label, this.findValue(field.key, "PSRC")))}
      </sh-card>
       <sh-card label='CSRC Data' id = "csrc" style = "width: 35%;">
      ${fields.map((field) => this.createField(field.label, this.findValue(field.key, "CSRC")))}
      </sh-card>
    `;
        this.requestUpdate();
    }
    displayTab() {
        switch (this.activeSection) {
            case EmcSection.systemStatus:
                this.systemStatusTabView();
                break;
            case EmcSection.testOptions:
                this.testOptionTabView();
                break;
            case EmcSection.armSubSystem:
                this.armSubSystemTabView();
                break;
            case EmcSection.driveSubSystem:
                this.driveSubSystemTabView();
                break;
            case EmcSection.csPdoSubSystem:
                this.csPdoSubSystemTabView();
                break;
            case EmcSection.psPdoSubSystem:
                this.psPdoSubSystemTabView();
                break;
            case EmcSection.patientSoftwareVersion:
                this.patientSoftwareVersionTabView();
                break;
            case EmcSection.controlSoftwareVersion:
                this.controlSoftwareVersionTabView();
                break;
            case EmcSection.pimData:
                this.pimDataTabView();
                break;
            case EmcSection.RcData:
                this.RcDataTabView();
                break;
            default:
                break;
        }
    }
    SystemTabs() {
        let controlSidePageContent = html ``;
        let patientSidePageContent = html ``;
        let pageContent = html ``;
        patientSidePageContent = html `<sh-tab-item label='Arm' ?active='${EmcSection.armSubSystem == this.activeSection}' @click="${this.armSubSystemTabView}"></sh-tab-item>
            <sh-tab-item label='Drive' ?active='${EmcSection.driveSubSystem == this.activeSection}' @click="${this.driveSubSystemTabView}"></sh-tab-item>
            <sh-tab-item label='PS CPU PDO' ?active='${EmcSection.psPdoSubSystem == this.activeSection}' @click="${this.psPdoSubSystemTabView}"></sh-tab-item>
            <sh-tab-item label='PS Version' ?active='${EmcSection.patientSoftwareVersion == this.activeSection}' @click="${this.patientSoftwareVersionTabView}"></sh-tab-item>`;
        controlSidePageContent = html `<sh-tab-item label='PIM' ?active='${EmcSection.pimData == this.activeSection}' @click="${this.pimDataTabView}"></sh-tab-item>
                          <sh-tab-item label='CS CPU PDO' ?active='${EmcSection.csPdoSubSystem == this.activeSection}' @click="${this.csPdoSubSystemTabView}"></sh-tab-item>              
                          <sh-tab-item label='CS Version' ?active='${EmcSection.controlSoftwareVersion == this.activeSection}' @click="${this.controlSoftwareVersionTabView}"></sh-tab-item>`;
        if (this.activeSystemsList.length == 2) {
            pageContent = html `${patientSidePageContent}
                          ${controlSidePageContent}`;
        }
        else if (this.activeSystemsList.length == 1 && this.activeSystemsList[0].isControlSide) {
            pageContent = html `${controlSidePageContent}`;
        }
        else if (this.activeSystemsList.length == 1 && !this.activeSystemsList[0].isControlSide) {
            pageContent = html `${patientSidePageContent}`;
        }
        else {
            pageContent = html ``;
        }
        return pageContent;
    }
    render() {
        const self = this;
        return html `
          <sh-page scrollable='true' theme="${this.currentTheme}">
            <sh-grid fit-content='true'>
              <sh-card columns='3' style="margin:0px;">
                <sh-card id='Top-Section-View' label=''>
                  <div style="display:flex; overflow: hidden">
                    <sh-card id='Start-Stop-Section' label=''>
                      <sh-radio-group horizontal='true'>
                        <div style="width: 120px; display:flex; overflow: hidden'">
                          <sh-radio-button label='Idle Monitor' ?disabled='${self.isEMCAutomationRunning}' ?active='${self.isIdleMonitorSelected}' @click='${() => self.selectIdleMonitor()}'></sh-radio-button>
                          <sh-radio-button label='EMC Automation' ?disabled='${(!self.isIdleMonitorRunning)}' ?active='${this.isEmcAutomationSelected}' @click='${() => self.selectEmcAutomation()}'></sh-radio-button>
                        </div>
                      </sh-radio-group>
                      <sh-button id='EMC-Start-Stop-Button' ?disabled='${self.isIdleMonitorSelected}' label='${(self.isEMCAutomationRunning ? 'Stop EMC Automation' : 'Start EMC Automation')}' size='l' style="padding: 0 8px;" @click=${() => self.onClickEmcAutomation()}></sh-button>
                      <sh-button id='Idle-Monitor-Button' ?disabled='${self.isEmcAutomationSelected}' label='${(self.isIdleMonitorRunning ? "Stop Idle Monitor" : "Start Idle Monitor")}' size='l' style="padding: 0 8px;" @click=${() => self.onClickIdleMonitor()}></sh-button>
                    </sh-card>
                    <sh-card id='Application-Running-Status-Section'>      
                      <sh-value-indicator size='s' value="${((self.isEMCAutomationRunning) || (self.isIdleMonitorRunning)) ? 'Yes' : 'No'}" label="${self.isEMCAutomationRunning ? 'EMC Running' : 'Idle Monitor Running'}" icon='monitor' icon-color='orange'></sh-value-indicator>
                      <sh-value-indicator size='s' id="Idle-Runtime-Indicator" value=${self.idleRuntime} label="Idle Monitor Runtime" unit="s"></sh-value-indicator>
                      <sh-value-indicator size='s' id="EMC-Runtime-Indicator" value=${self.emcAutomationRuntime} label="EMC Test Runtime" unit="s"></sh-value-indicator>
                    </sh-card>
                  </div>
                  <sh-modal label="${this.warningModalTitle}"
                          sticky
                          modal-height="250px"
                          modal-width="600px"
                          type="error"
                          ?visible=${self.hasWarning}>
                    <sh-button slot="footer" color="tertiary" label="Close" @click=${self.closeWarningBox}></sh-button>
                  </sh-modal>
                  <div>
                    <sh-button label='Manage Systems' size='l' style="margin-left: auto; margin-right: auto;"
                        ?disabled='${self.isIdleMonitorRunning}'
                        @click=${this.initiateAddSystems}>
                    </sh-button>
                    ${this.getActiveSystemsListDisplay()}
                  </div>
                    <sh-modal label='Add Systems'
                          sticky
                          modal-height="500x"
                          modal-width="1300px"
                          ?visible=${this.addingSystems}>
                    <div>
                        <sh-tag class="paddingAll" label="${self.activeSystemsList.length} selected"></sh-tag>
                        <sh-table id="add-systems-table">
                          <sh-table-row slot="header">
                              <sh-table-head unsortable columns="1">
                                  <sh-checkbox label="" disabled></sh-checkbox>
                              </sh-table-head>
                              <sh-table-head unsortable columns="6">System Name</sh-table-head>
                              <sh-table-head unsortable columns="5">Health System</sh-table-head>
                              <sh-table-head unsortable columns="4">Address</sh-table-head>
                              <sh-table-head unsortable columns="3">Type</sh-table-head>
                              <sh-table-head unsortable columns="5">Status</sh-table-head>
                          </sh-table-row>
                          <div style="overflow-y: auto; height: 500px;">
                          ${this.emcDisplayFacade.allSystemsList.map((system) => html `
                            <sh-table-row>
                                <sh-table-cell columns="1">
                                    <sh-checkbox label="" 
                                                  ?active="${this.activeSystemsList.find(activeSystem => activeSystem.systemId == system.systemId) != undefined}"
                                                  @checked="${(e) => this.systemSelectionChanged(e, system)}"></sh-checkbox>
                                </sh-table-cell>
                                <sh-table-cell columns="6">
                                    <sh-text>${system.systemName}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="5">
                                    <sh-text>${system.hospitalName}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="4">
                                    <sh-text>${system.address}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="3">
                                    <sh-text>${system.isControlSide ? 'Control-side' : 'Patient-side'}</sh-text>
                                </sh-table-cell>
                                <sh-table-cell columns="5">
                                    <cor-system-state state="${system.systemState}"></cor-system-state>
                                </sh-table-cell>
                            </sh-table-row>`)}
                            </div>
                        </sh-table>
                    </div>
                    <sh-button slot="footer" color="secondary" label="Close" @click=${self.closeAddSystems}></sh-button>
                    </sh-modal>
                </sh-card>
              </sh-card>
                <sh-card columns='9' style="margin-top:0px; margin-right:0px;">
                  <sh-card id='SubSystem-Data-Field-TabView-Section' label='' >
                    <sh-tabs>
                      <sh-tab-item label='System Status' ?active='${EmcSection.systemStatus == this.activeSection}' @click="${this.systemStatusTabView}"></sh-tab-item>
                      <sh-tab-item label='Test Options' ?active='${EmcSection.testOptions == this.activeSection}' @click="${this.testOptionTabView}"></sh-tab-item>
                      ${this.SystemTabs()}
                      <sh-tab-item label='RC' ?active='${EmcSection.RcData == this.activeSection}' @click="${this.RcDataTabView}"></sh-tab-item>
                    </sh-tabs>
                    ${this.displayTab()}
                    ${this.pageContents}
                  </sh-card>

                  <sh-card label="System Fault Message Viewer">
                    <div style="display:flex">
                      <sh-button slot='header' label="Export" icon='cloud-download' size='l' icon-color='blue' icon-position='left' tooltip='left' size='auto' color='secondary' @click=${() => this.emcDisplayFacade.exportMessages()}></sh-button>
                      <sh-button slot='header' label="Clear Messages" icon='delete-number' size='l' icon-color='red' icon-position='left' tooltip='right' size='auto' color='secondary' @click=${() => this.clearMessages()}></sh-button>
                    </div>
                    <div class="scrollable-container">
                      <textarea readonly style="width:100%; min-width:0; height:500px; resize:none; box-sizing:border-box; overflow-y:auto; overflow-x:hidden; white-space:pre-wrap; word-break:break-word; overflow-wrap:anywhere;">${this.messageText}</textarea>
                    </div>
                  </sh-card>
                </sh-card>
            </sh-grid>
          </sh-page>
          </div>
        `;
    }
    selectIdleMonitor() {
        this.isIdleMonitorSelected = true;
        this.isEmcAutomationSelected = false;
        this.requestUpdate();
    }
    selectEmcAutomation() {
        this.isIdleMonitorSelected = false;
        this.isEmcAutomationSelected = true;
        this.requestUpdate();
    }
    onClickIdleMonitor() {
        if (true == this.isIdleMonitorRunning) {
            // Run the stop logic
            this.isIdleMonitorRunning = false;
            this.emcDisplayFacade.setIdleMonitorSignal(false);
            this.setDefaultSubSystemSelections();
            this.emcDisplayFacade.clearAllSubSystemEmcSignals();
            this.logStopIdleMonitor();
            this.startIdleMonitorTimeStamp = 0;
        }
        else {
            // Run the start logic
            this.emcDisplayFacade.populateAllSystemList().then(() => {
                let allSystemsIDs = new Set();
                let toBeRemovedActiveSystems = [];
                this.emcDisplayFacade.allSystemsList.forEach(system => allSystemsIDs.add(system.systemId));
                this.activeSystemsList.forEach(activeSystem => {
                    if (false == allSystemsIDs.has(activeSystem.systemId)) {
                        toBeRemovedActiveSystems.push(activeSystem);
                    }
                });
                if (toBeRemovedActiveSystems.length > 0) {
                    this.hasWarning = true;
                    this.warningModalTitle = "Some active system(s) are in use elsewhere and hence removed.";
                    toBeRemovedActiveSystems.forEach(toBeRemovedSystem => {
                        this.activeSystemsList.splice(this.activeSystemsList.findIndex(activeSystem => activeSystem.systemId == toBeRemovedSystem.systemId), 1);
                        if (toBeRemovedSystem.isControlSide)
                            this.controlSystemCount--;
                        else
                            this.patientSystemCount--;
                    });
                }
                else if (this.activeSystemsList.length > 0) {
                    this.isIdleMonitorRunning = true;
                    this.emcDisplayFacade.setIdleMonitorSignal(true);
                    this.emcDisplayFacade.resetFaultsAndWarningsCount();
                    this.logStartIdleMonitor();
                    this.startIdleMonitorTimeStamp = Date.now();
                }
                else {
                    this.hasWarning = true;
                    this.warningModalTitle = "Select system(s) before starting idle monitor.";
                    this.callAfterWarningClose = () => { this.addingSystems = true; };
                }
            });
        }
        this.requestUpdate();
    }
    onClickEmcAutomation() {
        // Here basically we turn the emc mode on but do not tamper with idle monitor                     
        this.isEMCAutomationRunning = !this.isEMCAutomationRunning;
        if (true == this.isEMCAutomationRunning) {
            if (this.emcFullSystemSelected)
                this.emcDisplayFacade.setFullSystemEmcSignal();
            else {
                this.emcDisplayFacade.clearFullSystemEmcSignal();
                Object.values(Subsystems).forEach(element => {
                    this.emcDisplayFacade.setSubSystemSignal(element);
                });
            }
            this.emcDisplayFacade.setEmcAutomationSignal(true);
            this.logEmcAutomationStartTime();
            this.startEmcAutomationTimestamp = Date.now();
            this.emcDisplayFacade.startAudibleTone();
        }
        else {
            this.emcDisplayFacade.clearFullSystemEmcSignal();
            this.emcDisplayFacade.clearAllSubSystemEmcSignals();
            this.setDefaultSubSystemSelections();
            this.emcDisplayFacade.setEmcAutomationSignal(false);
            this.logEmcAutomationRuntime();
            this.startEmcAutomationTimestamp = 0;
            this.emcDisplayFacade.stopAudibleTone();
        }
        this.requestUpdate();
    }
    setDefaultSubSystemSelections() {
        this.emcDisplayFacade.armLock = false;
        this.emcDisplayFacade.RotJog1 = true;
        this.emcDisplayFacade.RotJog2 = true;
        this.emcDisplayFacade.RotJog3 = true;
        this.emcDisplayFacade.RotJog4 = true;
        this.emcDisplayFacade.moveAllLinearMotors = false;
    }
    enableFullSystemEmc() {
        this.emcFullSystemSelected = true;
        this.requestUpdate();
    }
    enableSubSystemEmc() {
        this.emcFullSystemSelected = false;
        this.requestUpdate();
    }
    toggleSubsystem(subsystem) {
        switch (subsystem) {
            case 'MoveAllLinearMotors':
                this.emcDisplayFacade.moveAllLinearMotors = !this.emcDisplayFacade.moveAllLinearMotors;
                break;
            case 'ArmLock':
                this.emcDisplayFacade.armLock = !this.emcDisplayFacade.armLock;
                break;
            case 'Mod1RotMotor':
                this.emcDisplayFacade.RotJog1 = !this.emcDisplayFacade.RotJog1;
                break;
            case 'Mod2RotMotor':
                this.emcDisplayFacade.RotJog2 = !this.emcDisplayFacade.RotJog2;
                break;
            case 'Mod3RotMotor':
                this.emcDisplayFacade.RotJog3 = !this.emcDisplayFacade.RotJog3;
                break;
            case 'Mod4RotMotor':
                this.emcDisplayFacade.RotJog4 = !this.emcDisplayFacade.RotJog4;
                break;
        }
        this.emcDisplayFacade.setSubSystemSignal(subsystem);
        this.requestUpdate();
    }
    toggleUserInput(userActionInput) {
        switch (userActionInput) {
            case 'Continue':
                this.userInput = true;
                break;
            case 'Stop':
                this.userInput = false;
                break;
        }
        this.emcDisplayFacade.setUserInputSignal(this.userInput);
        this.requestUpdate();
    }
    logStartIdleMonitor() {
        let currentDateTime = new Date().toLocaleString("en-US");
        this.emcDisplayFacade.addMessageToMessageDisplay(currentDateTime + " " + EMCDisplayMessageConsts.StartIdleMonitor);
    }
    logStopIdleMonitor() {
        let currentDateTime = new Date();
        this.emcDisplayFacade.addMessageToMessageDisplay(currentDateTime.toLocaleString("en-US") + " " + EMCDisplayMessageConsts.StopIdleMonitor);
        this.emcDisplayFacade.addMessageToMessageDisplay(currentDateTime.toLocaleString("en-US") + " " + EMCDisplayMessageConsts.IdleMonitorDuration + " " + ((Number(this.idleRuntime)) / 60) + " " + EMCDisplayMessageConsts.Mins);
    }
    logEmcAutomationStartTime() {
        let currentDateTime = new Date();
        this.emcDisplayFacade.addMessageToMessageDisplay(currentDateTime.toLocaleString("en-US") + " " + EMCDisplayMessageConsts.StartEmcLogs);
    }
    logEmcAutomationRuntime() {
        let currentDateTime = new Date();
        this.emcDisplayFacade.addMessageToMessageDisplay(currentDateTime.toLocaleString("en-US") + " " + EMCDisplayMessageConsts.EmcModeDuration + " " + ((Number(this.emcAutomationRuntime)) / 60) + " " + EMCDisplayMessageConsts.Mins);
    }
    initiateAddSystems() {
        this.addingSystems = true;
        this.emcDisplayFacade.populateAllSystemList().then(() => this.requestUpdate());
    }
    closeAddSystems() {
        this.addingSystems = false;
        this.requestUpdate();
    }
    closeWarningBox() {
        this.hasWarning = false;
        if (this.callAfterWarningClose)
            this.callAfterWarningClose();
        this.callAfterWarningClose = null;
        this.requestUpdate();
    }
    patientSideSelected(system) {
        if (this.patientSystemCount < 1) {
            this.patientSystemCount++;
            this.activeSystemsList.push(system);
        }
        else if (this.patientSystemCount >= 1) {
            this.addingSystems = false;
            this.hasWarning = true;
            this.warningModalTitle = "Only 1 Patient-Side system can be selected";
            this.callAfterWarningClose = () => { this.addingSystems = true; };
        }
    }
    controlSideSelected(system) {
        if (this.controlSystemCount < 1) {
            this.controlSystemCount++;
            this.activeSystemsList.push(system);
        }
        else if (this.controlSystemCount >= 1) {
            this.addingSystems = false;
            this.hasWarning = true;
            this.warningModalTitle = "Only 1 Control-Side system can be selected";
            this.callAfterWarningClose = () => { this.addingSystems = true; };
        }
    }
    getActiveSystemsListDisplay() {
        return this.activeSystemsList.length > 0 ? html ` <sh-table ?readonly="false" id="emc-systems-table">
                      <sh-table-row slot="header">
                        <sh-table-head unsortable columns="12">Name</sh-table-head>
                        <sh-table-head unsortable columns="12">System Id</sh-table-head>
                        <sh-table-head unsortable columns="12">Type</sh-table-head>
                      </sh-table-row><sh-table  id="query-table">
                      ${this.activeSystemsList.map((system) => html `
                        <sh-table-row columns="36" id="${system}">
                          <sh-table-cell columns="12">
                              <sh-text>${system.systemName}</sh-text>
                          </sh-table-cell>
                          <sh-table-cell columns="12">
                              <sh-text>${system.systemId}</sh-text>
                          </sh-table-cell>
                          <sh-table-cell columns="12">
                              <sh-text>${system.isControlSide ? 'Control-side' : 'Patient-side'}</sh-text>
                          </sh-table-cell>
                        </sh-table-row> `)}
                    </sh-table>` : html ``;
    }
    systemSelectionChanged(e, system) {
        // @ts-ignore
        if (e.currentTarget.active) {
            if (this.activeSystemsList.length == 2) {
                this.addingSystems = false;
                this.hasWarning = true;
                this.warningModalTitle = "More than 2 systems cannot be selected";
                this.callAfterWarningClose = () => { this.addingSystems = true; };
            }
            else {
                if (system.isControlSide) {
                    this.controlSideSelected(system);
                }
                else {
                    this.patientSideSelected(system);
                }
            }
            //@ts-ignore
            e.currentTarget.active = false;
        }
        else {
            if (system.isControlSide) {
                this.controlSystemCount--;
            }
            else {
                this.patientSystemCount--;
            }
            this.activeSystemsList.splice(this.activeSystemsList.findIndex(activeSystem => activeSystem.systemId == system.systemId), 1);
        }
        this.activeSystemsList.sort((a, b) => a.systemName.localeCompare(b.systemName));
        this.requestUpdate();
    }
    clearMessages() {
        this.emcDisplayFacade.messageText = '\n';
    }
    static fixDecimalValueDisplay(value) {
        if (null === value || undefined == value) {
            return "0.00";
        }
        value = value.toString();
        let index = value.indexOf('.');
        if ((-1 != index)) {
            value = value.substring(0, index) + value.substring(index, index + 3);
        }
        return value;
    }
};
__decorate([
    property()
], CorPageEmcTest.prototype, "pageContents", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "activeSection", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "addingSystems", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningModalTitle", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "hasWarning", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "callAfterWarningClose", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "messageText", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "isIdleMonitorSelected", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "isIdleMonitorRunning", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "idleRuntime", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "startIdleMonitorTimeStamp", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "isEmcAutomationSelected", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "isEMCAutomationRunning", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "emcAutomationRuntime", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "startEmcAutomationTimestamp", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "emcFullSystemSelected", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "activeSystemsList", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "patientSystemCount", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "controlSystemCount", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "patientSubsystemsVersionData", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "controlSubsystemsVersionData", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "arm", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "drive", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "frame", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "csPdo", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "psPdo", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "latency", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "controlConnectionStatus", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "communicationFaultCount", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "pim", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "userInput", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountDrive", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountDrive", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountArm", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountArm", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountFrame", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountFrame", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountPSRC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountPSRC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountPSIC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountPSIC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountPSUI", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountPSUI", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountPSSC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountPSSC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountCSRC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountCSRC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountCSIC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountCSIC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountCSUI", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountCSUI", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountCSSC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountCSSC", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "warningCountPIM", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "faultCountPIM", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "csrc", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "psrc", void 0);
__decorate([
    property()
], CorPageEmcTest.prototype, "currentTheme", null);
CorPageEmcTest = CorPageEmcTest_1 = __decorate([
    customElement('cor-emc-test')
], CorPageEmcTest);
export { CorPageEmcTest };
//# sourceMappingURL=CorPageEMCTest.js.map